import { Check, Close, EditOutlined } from 'components/icons';
import type { DataTableColumnDef } from 'components/tables/data-table';
import isEqual from 'lodash/isEqual';
import { FC, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

import PermissionButton from 'components/buttons/PermissionButton';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Checkbox } from 'components/ui/checkbox';
import { Textarea } from 'components/ui/textarea';
import { Tooltip } from 'components/ui/tooltip';
import {
    useLazyGetSystemPrivilegesWithFiltersQuery,
    useUpdateSystemPrivilegeDescriptionMutation,
    useUpdateSystemPrivilegeMinimumRequiredMutation,
    useUpdateSystemPrivilegeVisibleForMutation,
} from 'store/api/systemPrivileges';
import { useUpdateSystemRolePrivilegesMutation } from 'store/api/systemRoles';
import { useGetUserDataQuery } from 'store/api/user';
import { SystemPrivilegeListItem } from 'types/systemPrivileges';
import { SystemRole, SystemRolePrivilegeUpdateDto } from 'types/systemRoles';
import { errorAlert, successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import EditableVisibleForCell from './EditableVisibleForCell';
import styles from './RolesAndPermissionsTable.module.css';
import { NON_EDITABLE_ROLE_CODES, sortPermissionsByGroup } from './constants/rolesAndPermissionsConstants';
import { GHOST_ICON_BUTTON } from './ghostIconButton';

interface EditableDescriptionCellProps {
    id: number;
    description: string;
    canEdit: boolean;
    onSave: (id: number, description: string) => Promise<unknown>;
}

// Draft state lives inside the cell (not in the parent's columns memo) so typing in one row's
// editor doesn't rebuild the columns array and re-render every other row of the table.
const EditableDescriptionCell: FC<EditableDescriptionCellProps> = ({ id, description, canEdit, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    const confirm = useCallback(async () => {
        const trimmed = draft.trim();
        if (trimmed === '') return;
        try {
            await onSave(id, trimmed);
            successAlert(t('roles-and-permissions.description-updated'));
            setEditing(false);
        } catch {
            errorAlert(t('roles-and-permissions.description-update-error'));
        }
    }, [draft, id, onSave]);

    if (!canEdit) {
        return <>{description}</>;
    }

    if (!editing) {
        return (
            <div className="flex items-center gap-1">
                <span>{description}</span>
                <Tooltip content={t('roles-and-permissions.edit-description')}>
                    <button
                        type="button"
                        aria-label={t('roles-and-permissions.edit-description')}
                        className={cn(GHOST_ICON_BUTTON, 'text-grey-600')}
                        onClick={() => {
                            setDraft(description);
                            setEditing(true);
                        }}
                    >
                        <EditOutlined fontSize="small" />
                    </button>
                </Tooltip>
            </div>
        );
    }

    const isEmpty = draft.trim() === '';
    return (
        <div className="flex w-full items-start gap-1">
            <Textarea
                autoFocus
                rows={1}
                className="min-h-9 min-w-0 flex-1"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        if (!isEmpty) void confirm();
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        setEditing(false);
                    }
                }}
            />
            <button
                type="button"
                aria-label={t('save')}
                className={cn(GHOST_ICON_BUTTON, 'text-secondary')}
                disabled={isEmpty}
                onClick={() => void confirm()}
            >
                <Check fontSize="small" />
            </button>
            <button
                type="button"
                aria-label={t('cancel')}
                className={cn(GHOST_ICON_BUTTON, 'text-grey-600')}
                onClick={() => setEditing(false)}
            >
                <Close fontSize="small" />
            </button>
        </div>
    );
};

interface RolesAndPermissionsTableProps {
    portalRef: RefObject<HTMLDivElement | null>;
    systemRole: SystemRole;
    onSave?: () => void;
    associatedUsersHasChanges?: boolean;
    onSaveAssociatedUsers?: () => Promise<void>;
    administrativeRoleHasChanges?: boolean;
    onSaveAdministrativeRole?: () => Promise<void>;
}

const RolesAndPermissionsTable: FC<RolesAndPermissionsTableProps> = ({
    portalRef,
    systemRole,
    onSave,
    associatedUsersHasChanges = false,
    onSaveAssociatedUsers,
    administrativeRoleHasChanges = false,
    onSaveAdministrativeRole,
}) => {
    const [fetchData, { data, isLoading, isFetching }] = useLazyGetSystemPrivilegesWithFiltersQuery();
    const [updateSystemRolePrivileges, { isLoading: isUpdating }] = useUpdateSystemRolePrivilegesMutation();
    const [updateSystemPrivilegeDescription] = useUpdateSystemPrivilegeDescriptionMutation();
    const [updateMinimumRequired] = useUpdateSystemPrivilegeMinimumRequiredMutation();
    const [updateVisibleFor] = useUpdateSystemPrivilegeVisibleForMutation();
    const canEditDescription = useHasPermissions(['UPDATE.ROLES_AND_PERMISSIONS_DESCRIPTION']);
    const canEditPrivilegeParameters = useHasPermissions(['UPDATE.ROLES_AND_PERMISSIONS_EDIT_PRIVILEGE_PARAMETERS']);

    const { data: currentUser } = useGetUserDataQuery();
    const hasDefaultRolesPermission = useMemo(
        () => currentUser?.permissions?.some((p) => p.code === 'UPDATE.ROLES_AND_PERMISSIONS_DEFAULT_ROLES') ?? false,
        [currentUser]
    );
    // NON_EDITABLE_ROLE_CODES roles are read-only server-side unless the caller holds the
    // DEFAULT_ROLES permission, which bypasses that guard.
    const isEditingDisabled =
        (systemRole && NON_EDITABLE_ROLE_CODES.includes(systemRole.code) && !hasDefaultRolesPermission) ?? false;

    const [isHydrated, setIsHydrated] = useState(false);
    const [originalPermissions, setOriginalPermissions] = useState<Set<number>>(new Set());
    const [currentPermissions, setCurrentPermissions] = useState<Set<number>>(new Set());
    const [userChanges, setUserChanges] = useState<Map<number, boolean>>(new Map());
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- listed in useEffect deps below; converting to ref would break effect re-run on save->reset cycle
    const [shouldReset, setShouldReset] = useState(false);
    // Client-side quick filter on the LIVE (unsaved) effective-checked state; must stay client-side
    // so it reflects in-flight checkbox edits instead of the persisted server state.
    const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | null>(null);

    // TODO: cascading-set-state skip — multiple independent permission state machines (original/current/userChanges/reset) hydrated from async paginated data; refactor would require redesigning the permission-tracking model.
    useEffect(() => {
        if (!data?.content || isLoading || isFetching) return;

        if (shouldReset) {
            const enabled = new Set(data.content.filter((p) => p.isConnectedToRole).map((p) => p.id));
            setOriginalPermissions(enabled);
            setCurrentPermissions(enabled);
            setUserChanges(new Map());
            setShouldReset(false);
            return;
        }

        const newOriginal = new Set(originalPermissions);
        data.content.forEach((p) => {
            if (!originalPermissions.has(p.id) && p.isConnectedToRole) {
                newOriginal.add(p.id);
            }
        });

        if (!isEqual([...newOriginal], [...originalPermissions])) {
            setOriginalPermissions(newOriginal);
        }

        const newCurrent = new Set(currentPermissions);
        data.content.forEach((p) => {
            if (userChanges.has(p.id)) return;

            if (p.isConnectedToRole) {
                newCurrent.add(p.id);
            } else {
                newCurrent.delete(p.id);
            }
        });

        if (!isEqual([...newCurrent], [...currentPermissions])) {
            setCurrentPermissions(newCurrent);
        }
        setIsHydrated(true);
    }, [systemRole, data, shouldReset, userChanges, isLoading, isFetching, currentPermissions, originalPermissions]);

    const sortedData = useMemo(() => {
        if (!data?.content) return data;
        return { ...data, content: sortPermissionsByGroup(data.content) };
    }, [data]);

    const togglePermission = useCallback(
        (id: number) => {
            setUserChanges((prev) => {
                const next = new Map(prev);
                const newState = !currentPermissions.has(id);
                const originalState = originalPermissions.has(id);

                if (newState === originalState) {
                    next.delete(id);
                } else {
                    next.set(id, newState);
                }

                return next;
            });

            setCurrentPermissions((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [originalPermissions, currentPermissions]
    );

    const toggleAll = useCallback(
        (checked: boolean) => {
            if (!data?.content) return;

            const newUserChanges = new Map(userChanges);
            const newCurrentPermissions = new Set(currentPermissions);

            data.content.forEach(({ id, isMinimumRequiredPrivilege }) => {
                if (isMinimumRequiredPrivilege && !checked) return;

                if (checked) {
                    newCurrentPermissions.add(id);
                } else {
                    newCurrentPermissions.delete(id);
                }

                const originalState = originalPermissions.has(id);
                if (checked === originalState) {
                    newUserChanges.delete(id);
                } else {
                    newUserChanges.set(id, checked);
                }
            });

            setUserChanges(newUserChanges);
            setCurrentPermissions(newCurrentPermissions);
        },
        [data, userChanges, currentPermissions, originalPermissions]
    );

    // Codes that are currently checked in the live form (same source of truth as the per-row checkbox below).
    // A row is "superseded" when its supersededByCode is in this set, in which case it renders checked + disabled.
    const checkedCodes = useMemo(() => {
        const codes = new Set<string>();
        if (!data?.content) return codes;
        for (const item of data.content) {
            const checked = userChanges.has(item.id) ? userChanges.get(item.id) : item.isConnectedToRole;
            if (checked) codes.add(item.code);
        }
        return codes;
    }, [data, userChanges]);

    // Client-side quick filter: keep rows whose LIVE effective-checked state matches the toggle.
    // Effective-checked mirrors the checkbox cell exactly (own change/connection OR an active superseder).
    // Only the table's displayed rows are filtered here — the hydration effect and all/none logic keep
    // reading the full `data` set.
    const displayedData = useMemo(() => {
        if (!sortedData?.content || activeFilter === null) return sortedData;
        const content = sortedData.content.filter((item) => {
            const ownChecked = userChanges.has(item.id) ? userChanges.get(item.id) : item.isConnectedToRole;
            const effectiveChecked =
                ownChecked || (item.supersededByCode !== null && checkedCodes.has(item.supersededByCode));
            return activeFilter === 'active' ? effectiveChecked : !effectiveChecked;
        });
        return { ...sortedData, content };
    }, [sortedData, activeFilter, userChanges, checkedCodes]);

    const { allChecked, someChecked } = useMemo(() => {
        if (!data?.content?.length) return { allChecked: false, someChecked: false };

        let checkedCount = 0;
        for (const item of data.content) {
            const ownChecked = userChanges.has(item.id) ? userChanges.get(item.id) : item.isConnectedToRole;
            // Superseded rows count as checked for the all/none calculation even though they can't be toggled directly.
            const effectiveChecked =
                ownChecked || (item.supersededByCode !== null && checkedCodes.has(item.supersededByCode));
            if (effectiveChecked) checkedCount++;
        }

        return {
            allChecked: checkedCount === data.content.length,
            someChecked: checkedCount > 0 && checkedCount < data.content.length,
        };
    }, [data, userChanges, checkedCodes]);

    const isTableDirty = useMemo(() => {
        if (originalPermissions.size !== currentPermissions.size) return true;
        for (const id of originalPermissions) {
            if (!currentPermissions.has(id)) return true;
        }
        return false;
    }, [currentPermissions, originalPermissions]);

    const handleSave = useCallback(async () => {
        try {
            // Save permissions if there are table changes
            if (isTableDirty) {
                const updateDto: SystemRolePrivilegeUpdateDto = {
                    roleId: systemRole.id,
                    privileges: Array.from(userChanges).map(([id, isEnabled]) => ({ id, isEnabled })),
                };
                await updateSystemRolePrivileges(updateDto).unwrap();
                setShouldReset(true);
                onSave?.();
            }

            // Save associated users if there are changes
            if (associatedUsersHasChanges && onSaveAssociatedUsers) {
                await onSaveAssociatedUsers();
            }

            // Save administrative role if there are changes
            if (administrativeRoleHasChanges && onSaveAdministrativeRole) {
                await onSaveAdministrativeRole();
            }
        } catch (error) {
            console.error('Error saving changes:', error);
        }
    }, [
        isTableDirty,
        userChanges,
        updateSystemRolePrivileges,
        systemRole.id,
        onSave,
        associatedUsersHasChanges,
        onSaveAssociatedUsers,
        administrativeRoleHasChanges,
        onSaveAdministrativeRole,
    ]);

    const saveDescription = useCallback(
        (id: number, description: string) => updateSystemPrivilegeDescription({ id, description }).unwrap(),
        [updateSystemPrivilegeDescription]
    );

    const saveMinimumRequired = useCallback(
        async (id: number, next: boolean) => {
            try {
                await updateMinimumRequired({ id, isMinimumRequiredPrivilege: next }).unwrap();
                successAlert(t('roles-and-permissions.is-required-updated'));
            } catch {
                errorAlert(t('roles-and-permissions.is-required-update-error'));
            }
        },
        [updateMinimumRequired]
    );

    const saveVisibleFor = useCallback(
        (id: number, visibleFor: string | null) => updateVisibleFor({ id, visibleFor }).unwrap(),
        [updateVisibleFor]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'code' }, { selector: 'description' }],
        }),
        []
    );

    // Rendered in the table's quick-filter row (via quickFilterLeading) as standard quick-filter
    // checkboxes that behave like radio buttons: selecting one clears the other, and clearing both
    // shows all rows. Filtering is client-side (see displayedData) so it reflects the live, unsaved
    // checkbox state instead of the persisted server state.
    const activePermissionsFilter = useMemo(
        () => (
            <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 whitespace-nowrap select-none">
                    <Checkbox
                        variant="secondary"
                        checked={activeFilter === 'active'}
                        onCheckedChange={(checked) => setActiveFilter(checked ? 'active' : null)}
                    />
                    {t('roles-and-permissions.filters.active-permissions')}
                </label>
                <label className="flex items-center gap-2 whitespace-nowrap select-none">
                    <Checkbox
                        variant="secondary"
                        checked={activeFilter === 'inactive'}
                        onCheckedChange={(checked) => setActiveFilter(checked ? 'inactive' : null)}
                    />
                    {t('roles-and-permissions.filters.inactive-permissions')}
                </label>
            </div>
        ),
        [activeFilter]
    );

    const columns = useMemo<DataTableColumnDef<SystemPrivilegeListItem>[]>(
        () => [
            {
                header: () => (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            variant="secondary"
                            aria-label={t('enabled')}
                            checked={allChecked}
                            indeterminate={someChecked}
                            onCheckedChange={() => toggleAll(!allChecked)}
                            disabled={isEditingDisabled}
                        />
                        <span>{t('enabled')}</span>
                    </div>
                ),
                id: 'enabled',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 130,
                },
                cell: ({ row }) => {
                    const { id, isConnectedToRole, isMinimumRequiredPrivilege, supersededByCode } = row.original;
                    const ownChecked = userChanges.has(id) ? userChanges.get(id) : isConnectedToRole;
                    // A row is superseded only while its superseder is actually checked in the live form.
                    const supersededBy =
                        supersededByCode !== null && checkedCodes.has(supersededByCode) ? supersededByCode : null;
                    const checkbox = (
                        <Checkbox
                            variant="secondary"
                            aria-label={row.original.code}
                            checked={ownChecked || supersededBy !== null}
                            onCheckedChange={() => togglePermission(id)}
                            disabled={isEditingDisabled || supersededBy !== null}
                        />
                    );
                    return (
                        <div className="flex items-center gap-1">
                            {supersededBy !== null ? (
                                <Tooltip
                                    content={t('roles-and-permissions.permission-superseded', { code: supersededBy })}
                                >
                                    <span className="inline-flex">{checkbox}</span>
                                </Tooltip>
                            ) : (
                                checkbox
                            )}
                            {isMinimumRequiredPrivilege ? <span className="text-fg-secondary">*</span> : null}
                        </div>
                    );
                },
            },
            ...(canEditPrivilegeParameters
                ? ([
                      {
                          header: t('roles-and-permissions.is-required'),
                          id: 'isRequired',
                          enableSorting: false,
                          meta: {
                              widthWeight: 0.5,
                              minPx: 110,
                          },
                          cell: ({ row }) => (
                              <Checkbox
                                  variant="secondary"
                                  aria-label={t('roles-and-permissions.is-required')}
                                  checked={!!row.original.isMinimumRequiredPrivilege}
                                  onCheckedChange={() =>
                                      void saveMinimumRequired(
                                          row.original.id,
                                          !row.original.isMinimumRequiredPrivilege
                                      )
                                  }
                              />
                          ),
                      },
                      {
                          header: t('roles-and-permissions.visible-for'),
                          id: 'visibleFor',
                          enableSorting: false,
                          meta: {
                              // Low weight + pixel floor keeps this near its content width (~develop's
                              // 178px), regardless of viewport, instead of growing with a weighted
                              // share; the freed width goes to the description column. While a cell here
                              // is being edited the whole column grows to fit the wide editor — done in
                              // CSS (RolesAndPermissionsTable.module.css), NOT by changing this width,
                              // which would rebuild the columns and close the open editor.
                              widthWeight: 0.2,
                              minPx: 178,
                          },
                          cell: ({ row }) => (
                              <EditableVisibleForCell
                                  id={row.original.id}
                                  visibleFor={row.original.visibleFor}
                                  onSave={saveVisibleFor}
                              />
                          ),
                      },
                  ] as DataTableColumnDef<SystemPrivilegeListItem>[])
                : []),
            {
                header: t('roles-and-permissions.code'),
                id: 'code',
                enableSorting: false,
                meta: {
                    widthWeight: 1.8,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    return row.original?.code;
                },
            },
            {
                header: t('description'),
                id: 'description',
                enableSorting: false,
                meta: {
                    // Widest column; absorbs whatever width the others leave.
                    widthWeight: 5,
                    minPx: 320,
                },
                cell: ({ row }) => (
                    <EditableDescriptionCell
                        id={row.original.id}
                        description={row.original.description}
                        canEdit={canEditDescription}
                        onSave={saveDescription}
                    />
                ),
            },
        ],
        [
            userChanges,
            togglePermission,
            isEditingDisabled,
            allChecked,
            someChecked,
            toggleAll,
            checkedCodes,
            canEditDescription,
            saveDescription,
            canEditPrivilegeParameters,
            saveMinimumRequired,
            saveVisibleFor,
        ]
    );

    return (
        <>
            <ListTableWithFilters<SystemPrivilegeListItem>
                // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
                // stay OFF (flags default false). This table lives in the Settings tabs layout, which
                // grows to content and never hands the table a bounded scroller — so we cap the
                // container height here. That makes it the scroll element the virtualizer measures, so
                // row windowing actually engages (only ~viewport rows render) instead of all ~330,
                // which is what makes checkbox toggles and edit-mode row resizing snappy.
                columnConfigId="roles-and-permissions"
                dataTableClassName={cn('max-h-[calc(100vh-20rem)]', styles.visibleForGrowOnEdit)}
                data={displayedData}
                columns={columns}
                fetchData={(paginationProps) => {
                    setIsHydrated(false);
                    return fetchData({ ...paginationProps, id: systemRole.id });
                }}
                isLoading={isLoading || isFetching || !isHydrated || isUpdating}
                rowsPerPage={500}
                filterConfig={filterConfig}
                quickFilterLeading={activePermissionsFilter}
                pluralTextId="roles-and-permissions.permissions"
                singularTextId="roles-and-permissions.permission"
            />
            {portalRef.current != null
                ? createPortal(
                      <PermissionButton
                          permissions={['UPDATE.ROLES_AND_PERMISSIONS']}
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => {
                              void handleSave();
                          }}
                          disabled={
                              (!isTableDirty && !associatedUsersHasChanges && !administrativeRoleHasChanges) ||
                              isUpdating
                          }
                      >
                          <FormattedMessage id="save" />
                      </PermissionButton>,
                      portalRef.current
                  )
                : null}
        </>
    );
};

export default RolesAndPermissionsTable;
